import { createSelector } from "reselect"
import { ReduxState, RateTable, NestedLaborRate } from "../common/types"
import { SerializedRateTable, SerializedLaborRate, flattenCustomFields } from "@rhumbix/rmbx_matching"
import { tEntitySelection } from "../cached-data/types"

/**
 * Selects the rate tables entity state from Redux.
 */
const selectRateTablesById = (state: ReduxState): tEntitySelection["rateTables"] =>
    state.entities.rateTables?.objects ?? {}

/**
 * Selector to get the cached labor rates from Redux state.
 */
export const selectLaborRatesById = (state: ReduxState): tEntitySelection["laborRates"] =>
    state.entities.laborRates?.objects ?? {}

/**
 * Transforms a LaborRate object from a cached RateTable's labor_rates field into the format
 * expected by the matching library.
 *
 * Uses {@link flattenCustomFields} from rmbx_matching to convert the nested custom_fields
 * structure (keyed by model type, then field name) into a flat CustomFieldCriterion[] array.
 */
const transformLaborRateForMatching = (laborRate: NestedLaborRate): SerializedLaborRate => {
    // Build modifier_custom_ids from custom_modifier_* fields
    const modifierCustomIds: Record<string, number[]> = {}

    // Extract any custom_modifier_* fields
    Object.entries(laborRate).forEach(([key, value]) => {
        if (key.startsWith("custom_modifier_") && Array.isArray(value) && value.length > 0) {
            const modifierName = key.replace("custom_modifier_", "")
            modifierCustomIds[modifierName] = value as number[]
        }
    })

    return {
        id: laborRate.id,
        row_order: laborRate.row_order,
        name: laborRate.name,
        time_type: laborRate.time_type,
        base_rate: laborRate.base_rate,
        custom_rates: laborRate.custom_rates ?? undefined,
        trades: laborRate.trades ?? [],
        classifications: laborRate.classifications ?? [],
        modifier_equipment_ids: laborRate.equipment_modifiers,
        modifier_custom_ids: Object.keys(modifierCustomIds).length > 0 ? modifierCustomIds : undefined,
        union_ids: laborRate.unions ?? [],
        cohort_ids: laborRate.cohorts ?? [],
        custom_fields: flattenCustomFields(laborRate.custom_fields),
        certification_ids: laborRate.certifications,
        license_ids: laborRate.licenses,
        project_client_names: [], // TODO: WA-9210 add Project - Client Name to match criteria
    }
}

/**
 * Transforms a cached RateTable from Redux into the format expected by the matching library.
 *
 * @param rateTable - The rate table from the Redux entity cache
 */
const transformRateTableForMatching = (rateTable: RateTable): SerializedRateTable => {
    return {
        id: rateTable.id,
        name: rateTable.name,
        version: rateTable.version,
        labor_rates: rateTable.labor_rates?.map(transformLaborRateForMatching) ?? [],
    }
}

/**
 * Returns a map of rate table IDs to serialized rate tables, like this:
 * {
 *    <id>: <SerializedRateTable>
 * }
 *
 * Custom field criteria on labor rates are flattened from the nested structure
 * (keyed by model type) into flat CustomFieldCriterion[] arrays using
 * {@link flattenCustomFields} from rmbx_matching.
 */
export const selectRateTablesForMatchingById = createSelector(
    [selectRateTablesById],
    (rateTables): Partial<Record<number, SerializedRateTable>> => {
        return Object.values(rateTables).reduce((acc, rateTable) => {
            acc[rateTable.id] = transformRateTableForMatching(rateTable)
            return acc
        }, {} as Partial<Record<number, SerializedRateTable>>)
    }
)
